import { extractApiErrorMessage, extractApiSuccessMessage } from '@/utils/dataNormalization';

export const openDocumentCreateModal = (state, { source = 'table' } = {}) => {
  state.documentModalMode = 'create';

  if (Object.prototype.hasOwnProperty.call(state, 'documentModalSource')) {
    state.documentModalSource = source;
  }

  state.editingDocument = null;
  state.showCreateModal = true;
};

export const openDocumentEditModal = (state, row, { source = 'table' } = {}) => {
  const hasDocument = Number(row?.id || 0) > 0;
  state.documentModalMode = hasDocument ? 'edit' : 'create';

  if (Object.prototype.hasOwnProperty.call(state, 'documentModalSource')) {
    state.documentModalSource = source;
  }

  state.editingDocument = {
    ...(row && typeof row === 'object' ? row : {}),
    name: String(row?.name || row?.fileName || '').trim(),
  };
  state.showCreateModal = true;
};

export const resetDocumentModalState = (state, { source = 'table' } = {}) => {
  state.documentModalMode = 'create';

  if (Object.prototype.hasOwnProperty.call(state, 'documentModalSource')) {
    state.documentModalSource = source;
  }

  state.editingDocument = null;
};

export const openDocumentDeleteModal = (state, row) => {
  state.pendingDeleteDocument = row && typeof row === 'object' ? { ...row } : null;
  state.isDeleteConfirmModalOpen = true;
};

export const closeDocumentDeleteModal = (state) => {
  if (state.deletingDocument) {
    return false;
  }

  state.isDeleteConfirmModalOpen = false;
  state.pendingDeleteDocument = null;
  return true;
};

export const resolveDocumentFormValues = (payload = {}) => ({
  name: String(payload?.name || '').trim(),
  sourceType: String(payload?.sourceType || 'file').trim(),
  description: String(payload?.comments || '').trim(),
  startDate: String(payload?.validityStart || '').trim(),
  endDate: String(payload?.validityEnd || '').trim(),
  url: String(payload?.url || '').trim(),
  file: payload?.file || null,
});

export const buildDocumentUpdatePayload = ({
  editingDocument = null,
  formValues = {},
  recordId = 0,
} = {}) => {
  const isFile = Number(editingDocument?.isFile || 0) === 1 ? 1 : 0;

  const updatePayload = {
    name: formValues.name,
    source_type: isFile === 1 ? 'file' : 'url',
    is_file: isFile,
    description: formValues.description,
    start_date: formValues.startDate,
    end_date: formValues.endDate,
  };

  if (Number(recordId || 0) > 0) {
    updatePayload.record_id = Number(recordId || 0);
  }

  if (isFile === 0) {
    updatePayload.url = String(editingDocument?.documentRaw || formValues.url || '').trim();
  }

  return updatePayload;
};

export const buildDocumentCreateFormData = ({
  formValues = {},
  recordId = 0,
} = {}) => {
  const formData = new FormData();

  formData.append('name', formValues.name);
  formData.append('source_type', formValues.sourceType);
  formData.append('description', formValues.description);
  formData.append('start_date', formValues.startDate);
  formData.append('end_date', formValues.endDate);

  if (Number(recordId || 0) > 0) {
    formData.append('record_id', String(Number(recordId || 0)));
  }

  if (formValues.sourceType === 'url') {
    formData.append('is_file', '0');
    formData.append('url', formValues.url);
  } else {
    formData.append('is_file', '1');
    if (formValues.file) {
      formData.append('document', formValues.file);
    }
  }

  return formData;
};

export const submitDocumentCrud = async ({
  mode = 'create',
  editingDocument = null,
  payload = {},
  recordId = 0,
  updateRequest,
  createRequest,
  toast,
  toasts = {},
} = {}) => {
  const safeMode = String(mode || 'create').trim().toLowerCase();
  const editingId = Number(editingDocument?.id || 0);
  const isEditRequest = safeMode === 'edit' && editingId > 0;
  const formValues = resolveDocumentFormValues(payload);
  const fallbackError = isEditRequest ? toasts.updateError : toasts.saveError;

  try {
    const request = isEditRequest
      ? updateRequest(
        editingId,
        buildDocumentUpdatePayload({ editingDocument, formValues, recordId })
      )
      : createRequest(
        buildDocumentCreateFormData({ formValues, recordId }),
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

    const response = await request;
    const apiErrorMessage = extractApiErrorMessage(response, fallbackError);

    if (apiErrorMessage) {
      toast?.error(apiErrorMessage);
      return { ok: false, isEditRequest, response, apiErrorMessage };
    }

    toast?.success(extractApiSuccessMessage(
      response,
      isEditRequest ? toasts.updateSuccess : toasts.saveSuccess
    ));

    return { ok: true, isEditRequest, response };
  } catch (error) {
    toast?.error(extractApiErrorMessage(error, fallbackError));
    return { ok: false, isEditRequest, error };
  }
};

export const confirmDocumentDelete = async ({
  parentId = 0,
  documentId = 0,
  deleteRequest,
  toast,
  successMessage = '',
  errorMessage = '',
} = {}) => {
  const normalizedParentId = Number(parentId || 0);
  const normalizedDocumentId = Number(documentId || 0);

  if (normalizedParentId <= 0 || normalizedDocumentId <= 0) {
    return { ok: false, skipped: true };
  }

  try {
    const response = await deleteRequest(normalizedParentId, normalizedDocumentId);
    toast?.success(extractApiSuccessMessage(response, successMessage));
    return { ok: true, response };
  } catch (error) {
    toast?.error(extractApiErrorMessage(error, errorMessage));
    return { ok: false, error };
  }
};
